import React, { useEffect, useState } from 'react';
import { motion, useTransform, MotionValue } from 'framer-motion';
interface Triangle {
  id: number;
  size: number;
  color: string;
  initialX: number;
  initialY: number;
  duration: number;
  rotationStart: number;
  rotationEnd: number;
  convergenceStart: number;
  convergenceEnd: number;
  isLogoTriangle?: boolean;
  logoPoints?: string;
  logoColor?: string;
}
interface AnimatedTrianglesProps {
  scrollProgress: MotionValue<number>;
}
interface TriangleItemProps {
  triangle: Triangle;
  scrollProgress: MotionValue<number>;
}
const COLORS = ['#A1DA52', '#80BA32', '#5992D2', '#3F75B8', '#2F5890', '#1E3E65'];
// Logo size for final frame
const LOGO_SIZE = 200;
const LOGO_VIEWBOX_WIDTH = 170;
const LOGO_VIEWBOX_HEIGHT = 160;
// Logo triangles with exact SVG coordinates
const LOGO_TRIANGLES = [
// Row 2 - 2 medium green triangles pointing UP
{
  points: '60,54.64 40,89.28 80,89.28',
  color: '#7CB342'
}, {
  points: '100,54.64 80,89.28 120,89.28',
  color: '#7CB342'
},
// Row 1 - 3 light green triangles pointing DOWN
{
  points: '40,89.28 20,54.64 60,54.64',
  color: '#A1DA52'
}, {
  points: '80,89.28 60,54.64 100,54.64',
  color: '#A1DA52'
}, {
  points: '120,89.28 100,54.64 140,54.64',
  color: '#A1DA52'
},
// Row 3 - 2 blue triangles pointing DOWN + 1 middle triangle
{
  points: '60,123.92 40,89.28 80,89.28',
  color: '#5992D2'
}, {
  points: '100,123.92 80,89.28 120,89.28',
  color: '#5992D2'
}, {
  points: '80,89.28 60,123.92 100,123.92',
  color: '#3F75B8'
},
// Row 4 - 1 dark blue triangle pointing UP
{
  points: '80,158.56 100,123.92 60,123.92',
  color: '#1E3E65'
}];
function TriangleItem({
  triangle: t,
  scrollProgress
}: TriangleItemProps) {
  if (t.isLogoTriangle) {
    // Logo triangles: scattered → center → exact logo position
    const leftPosition = useTransform(scrollProgress, [t.convergenceStart, t.convergenceEnd, 0.75, 1], [`${t.initialX}%`, '50%', '50%', '50%']);
    const topPosition = useTransform(scrollProgress, [t.convergenceStart, t.convergenceEnd, 0.75, 1], [`${t.initialY}%`, '50%', '50%', '50%']);
    const rotation = useTransform(scrollProgress, [0, 0.75, 1], [t.rotationStart, t.rotationStart, 0]);
    const opacity = useTransform(scrollProgress, [0, 0.75, 0.85, 1], [0.15, 0.15, 0.95, 1]);
    // Scale factor to convert from scattered size to logo size
    const scale = useTransform(scrollProgress, [0, 0.75, 1], [1, 1, LOGO_SIZE / 100]);
    return <motion.div className="absolute" style={{
      left: leftPosition,
      top: topPosition,
      opacity: opacity,
      rotate: rotation,
      scale: scale,
      x: '-50%',
      y: '-50%'
    }} data-id="element-10">
        <svg viewBox={`0 0 ${LOGO_VIEWBOX_WIDTH} ${LOGO_VIEWBOX_HEIGHT}`} className="w-full h-full" style={{
        width: LOGO_SIZE,
        height: LOGO_SIZE
      }} data-id="element-11">
          <polygon points={t.logoPoints} fill={t.logoColor} data-id="element-12" />
        </svg>
      </motion.div>;
  }
  // Regular triangles: Enhanced organic motion
  const leftPosition = useTransform(scrollProgress, [t.convergenceStart, t.convergenceEnd], [`${t.initialX}%`, '50%']);
  const topPosition = useTransform(scrollProgress, [t.convergenceStart, t.convergenceEnd], [`${t.initialY}%`, '50%']);
  // Wrapper opacity handles the scroll-based fade out (1 -> 0)
  const wrapperOpacity = useTransform(scrollProgress, [0, 0.75, 0.85], [1, 1, 0]);
  // Generate deterministic random variations based on triangle ID
  const seed = t.id * 17;
  // Motion parameters
  const driftX = 20 + seed % 30;
  const driftY = 20 + seed * 2 % 30;
  const driftDuration = 15 + seed % 15;
  // Some triangles rotate faster for variety
  const isFastRotator = t.id % 5 === 0;
  const rotateDuration = isFastRotator ? 8 + seed % 7 : t.duration;
  const scaleDuration = 4 + seed % 5;
  const opacityDuration = 3 + seed % 4;
  return <motion.div className="absolute -translate-x-1/2 -translate-y-1/2" style={{
    left: leftPosition,
    top: topPosition,
    width: t.size,
    height: t.size,
    opacity: wrapperOpacity
  }} animate={{
    x: [0, driftX, 0, -driftX, 0],
    y: [0, -driftY, 0, driftY, 0],
    rotate: [t.rotationStart, t.rotationEnd]
  }} transition={{
    x: {
      duration: driftDuration,
      repeat: Infinity,
      ease: 'easeInOut',
      times: [0, 0.25, 0.5, 0.75, 1]
    },
    y: {
      duration: driftDuration * 1.2,
      repeat: Infinity,
      ease: 'easeInOut',
      times: [0, 0.25, 0.5, 0.75, 1]
    },
    rotate: {
      duration: rotateDuration,
      repeat: Infinity,
      repeatType: 'mirror',
      ease: 'linear'
    }
  }} data-id="element-13">
      <motion.svg viewBox="0 0 100 100" className="w-full h-full" animate={{
      scale: [0.95, 1.05, 0.95],
      opacity: [0.1, 0.25, 0.1]
    }} transition={{
      scale: {
        duration: scaleDuration,
        repeat: Infinity,
        ease: 'easeInOut'
      },
      opacity: {
        duration: opacityDuration,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }} data-id="element-14">
        <polygon points="50,10 90,90 10,90" fill={t.color} data-id="element-15" />
      </motion.svg>
    </motion.div>;
}
export function AnimatedTriangles({
  scrollProgress
}: AnimatedTrianglesProps) {
  const [triangles, setTriangles] = useState<Triangle[]>([]);
  useEffect(() => {
    const count = 80;
    const newTriangles: Triangle[] = [];
    // Create 9 logo triangles using exact SVG coordinates
    LOGO_TRIANGLES.forEach((logo, index) => {
      const convergenceStart = Math.random() * 0.3;
      const convergenceDuration = Math.random() * 0.3 + 0.2;
      newTriangles.push({
        id: index,
        size: Math.random() * 60 + 20,
        color: logo.color,
        initialX: Math.random() * 100,
        initialY: Math.random() * 100,
        duration: Math.random() * 20 + 20,
        rotationStart: Math.random() * 360,
        rotationEnd: Math.random() * 360,
        convergenceStart: convergenceStart,
        convergenceEnd: Math.min(convergenceStart + convergenceDuration, 0.7),
        isLogoTriangle: true,
        logoPoints: logo.points,
        logoColor: logo.color
      });
    });
    // Create remaining regular triangles
    for (let i = LOGO_TRIANGLES.length; i < count; i++) {
      const convergenceStart = Math.random() * 0.4;
      const convergenceDuration = Math.random() * 0.4 + 0.3;
      newTriangles.push({
        id: i,
        size: Math.random() * 60 + 20,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        initialX: Math.random() * 100,
        initialY: Math.random() * 100,
        duration: Math.random() * 20 + 20,
        rotationStart: Math.random() * 360,
        rotationEnd: Math.random() * 360 + (Math.random() > 0.5 ? 360 : -360),
        convergenceStart: convergenceStart,
        convergenceEnd: Math.min(convergenceStart + convergenceDuration, 1)
      });
    }
    setTriangles(newTriangles);
  }, []);
  return <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#042854] w-screen h-screen" data-id="element-16">
      {triangles.map(t => <TriangleItem key={t.id} triangle={t} scrollProgress={scrollProgress} data-id="element-17" />)}
    </div>;
}